import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient, useApiRole } from '../contexts/ApiContext';
import { mapResponse } from './helpers';
import { sortBy } from 'lodash-es';
import {
  ApiCreateMember,
  ApiError,
  ApiMemberRole,
  DeleteMemberRequest,
  ListMembersResponse,
} from '../openapi';
import { createIdempotencyKey } from '../hooks/useIdempotencyKey';
import { useHasRole } from './access';

const roleSortOrder: Record<ApiMemberRole, number> = {
  OWNER: 1,
  ADMIN: 2,
  PAYER: 3,
  VIEWER: 4,
  MEMBER: 5,
};

export function useBusinessMembersQuery() {
  const role = useApiRole();
  const client = useApiClient();
  return useQuery<ListMembersResponse>({
    queryKey: ['businesses', 'current', 'members'],
    queryFn: ({ signal }) =>
      client
        .GET('/businesses/current/members', { signal })
        .then(mapResponse)
        .then(({ members }) => ({
          members: sortBy(members, (member) => roleSortOrder[member.membership.role]),
        })),
    enabled: role !== 'MEMBER',
  });
}

export function useBusinessMemberQuery({ customerId }: { customerId: string }) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['businesses', 'current', 'members', customerId],
    queryFn: ({ signal }) =>
      client
        .GET('/businesses/current/members', { signal })
        .then(mapResponse)
        .then(({ members }) => {
          const member = members.find((member) => member.customer.id === customerId);
          if (!member) {
            throw new ApiError('Member not found', 404);
          }
          return member;
        }),
  });
}

export function useAddBusinessMemberMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['businesses', 'current', 'members', 'add'],
    mutationFn: (body: ApiCreateMember) =>
      client
        .PUT('/businesses/current/members', {
          body: {
            members: [body],
          },
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then(mapResponse),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['businesses', 'current', 'members'] }),
  });
}

export function useUpdateBusinessMemberMutation({ customerId }: { customerId: string }) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const isOwner = useHasRole('OWNER');
  return useMutation({
    mutationKey: ['businesses', 'current', 'members', customerId, 'update'],
    mutationFn: async (body: ApiCreateMember) => {
      if (isOwner && body.role) {
        await client
          .PUT('/businesses/current/members', {
            body: {
              members: [
                {
                  role: body.role,
                  email: body.email,
                },
              ],
            },
            headers: { 'Idempotency-Key': createIdempotencyKey() },
          })
          .then(mapResponse);
      }

      if (body.dob || body.legalName || body.nationality) {
        await client
          .PUT('/businesses/current/members/{id}/legalInfo', {
            params: { path: { id: customerId } as never },
            body: {
              dob: body.dob || undefined,
              legalName: body.legalName || undefined,
              nationality: body.nationality || undefined,
            },
            headers: { 'Idempotency-Key': createIdempotencyKey() },
          })
          .then(mapResponse);
      }

      return null;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['businesses', 'current', 'members'] }),
  });
}

export function useDeleteBusinessMember({ idempotencyKey }: { idempotencyKey: string }) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['businesses', 'current', 'members', 'remove'],
    mutationFn: (body: DeleteMemberRequest) =>
      client
        .POST('/businesses/current/members/remove', {
          body,
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then(() => null),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['businesses', 'current', 'members'] }),
  });
}
